import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';

// Dev: connect to localhost:3000 (backend)
// Production: connect to the API server (Railway) since the PWA is on Cloudflare Pages (different origin)
const SOCKET_URL = import.meta.env.VITE_SOCKET_URL
  || (import.meta.env.DEV ? 'http://localhost:3000' : 'https://ramba.qaysariya.com');

export function useSocket(shopId: string | undefined) {
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!shopId) return;

    const token = localStorage.getItem('shop_token');
    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socket.on('connect', () => {
      setIsConnected(true);
      const channel = `queue:shop:${shopId}`;
      socket.emit('subscribe', { channel });
      console.log(`[Socket.IO] Connected: ${socket.id}, subscribed to ${channel}`);
    });

    // Debug: log ALL incoming events
    socket.onAny((event, data) => {
      console.log(`[Socket.IO] Event received: ${event}`, data);
    });

    socket.on('disconnect', () => {
      setIsConnected(false);
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [shopId]);

  return { socket: socketRef.current, isConnected };
}

export function useSocketEvent<T = unknown>(
  socket: Socket | null,
  event: string,
  callback: (data: T) => void
) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!socket) return;

    const handler = (data: T) => callbackRef.current(data);
    socket.on(event, handler);

    return () => {
      socket.off(event, handler);
    };
  }, [socket, event]);
}
